import { useEffect, useMemo, useState } from "react";
import { calcScore } from "../../../../../components/strength-bar/strength-bar";
import { authApi } from "../../../../../api";
import { useGlobalNotifications } from "../../../../../hooks/use-global-notifications";
import { useAuth } from "../../../../../contexts/auth/auth-context";

export type UseChangePasswordPanelArgs = {
    // ⬇ allow undefined explicitly because exactOptionalPropertyTypes is on
    onPasswordChanged?: (() => void) | undefined;
    handleBack?: (() => void) | undefined;
    onCancel?: (() => void) | undefined;
};

type PasswordReqs = {
    length: boolean;
    upper: boolean;
    lower: boolean;
    number: boolean;
    symbol: boolean;
};

export function useChangePasswordPanel({
    // onPasswordChanged,
    onCancel,
    handleBack,
}: UseChangePasswordPanelArgs) {
    const { showError, showSuccess, showLoading, hideNotification } =
        useGlobalNotifications();
    const { signOut } = useAuth();

    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [triedSubmit, setTriedSubmit] = useState(false);

    // eye toggles
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // password rules
    const reqs: PasswordReqs = {
        length: next.length >= 8,
        upper: /[A-Z]/.test(next),
        lower: /[a-z]/.test(next),
        number: /\d/.test(next),
        symbol: /[^A-Za-z0-9]/.test(next),
    };

    const checklistOk = Object.values(reqs).every(Boolean);
    const strengthScore = calcScore(next);
    const strongEnoughScore = strengthScore >= 10;
    const isTooWeak = next.length > 0 && (!checklistOk || !strongEnoughScore);
    const passwordsMatch = next.length > 0 && next === confirm;

    const disabled = useMemo(
        () =>
            !current || !next || !confirm || !passwordsMatch || isTooWeak ||
            saving,
        [current, next, confirm, passwordsMatch, isTooWeak, saving],
    );

    useEffect(() => {
        if (error) setError(null);
        // only clear API errors when editing inputs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current, next, confirm]);

    const handleSubmit = async () => {
        setTriedSubmit(true);
        setError(null);

        // Validate all fields are filled
        if (!current) {
            setError("Please enter your current password.");
            showError("Please enter your current password.");
            return;
        }
        if (!next) {
            setError("Please enter a new password.");
            showError("Please enter a new password.");
            return;
        }
        if (!confirm) {
            setError("Please confirm your new password.");
            showError("Please confirm your new password.");
            return;
        }
        if (!passwordsMatch) {
            setError("New passwords do not match.");
            showError("New passwords do not match.");
            return;
        }
        if (isTooWeak) {
            setError(
                "Password too weak. Add upper/lower case letters, numbers and symbols.",
            );
            showError("Password too weak. Please meet all requirements.");
            return;
        }

        try {
            setSaving(true);
            showLoading("Updating your password...");

            // Ensure we have an access token before making the request
            const token = localStorage.getItem("access_token");
            if (!token) {
                throw new Error(
                    "You are not logged in. Please sign in and try again.",
                );
            }

            await authApi.updatePassword(current, next);
            setSaving(false);
            hideNotification();
            showSuccess(
                "Password changed. Please sign in with your new password.",
            );

            // Password change invalidates the session server-side.
            // Sign out locally so the user is redirected to login.
            setTimeout(async () => {
                try {
                    await signOut();
                } catch {
                    // If backend signout fails (token already dead), force local cleanup
                    window.location.href = "/";
                }
            }, 1500);
        } catch (err: any) {
            setSaving(false);
            hideNotification();

            // Parse error from backend
            let errorMsg = "Could not change password. Please try again.";

            if (err?.response?.data?.detail) {
                const detail = err.response.data.detail;
                if (Array.isArray(detail)) {
                    // Pydantic validation error is an array of objects
                    errorMsg = detail.map((e: any) => e.msg).join(". ");
                } else if (typeof detail === "string") {
                    errorMsg = detail;
                } else {
                    errorMsg = JSON.stringify(detail);
                }
            } else if (
                err?.response?.status === 403 || err?.response?.status === 401
            ) {
                errorMsg = "Current password is incorrect.";
            } else if (err?.response?.status === 400) {
                errorMsg = "Invalid password. Please check your input.";
            } else if (err?.message) {
                errorMsg = err.message;
            }

            setError(errorMsg);
            showError(errorMsg);
        }
    };

    const handleCancel = () => {
        // if parent passed a handler (close panel), call it; otherwise just reset fields
        if (onCancel) {
            onCancel();
            return;
        }
        setCurrent("");
        setNext("");
        setConfirm("");
        setShowCurrent(false);
        setShowNext(false);
        setShowConfirm(false);
        setError(null);
        setTriedSubmit(false);
        handleBack && handleBack();
    };

    return {
        // values
        current,
        next,
        confirm,
        error,
        saving,
        triedSubmit,
        reqs,
        strengthScore,
        isTooWeak,
        passwordsMatch,
        disabled,

        // eye toggles
        showCurrent,
        showNext,
        showConfirm,

        // setters
        setCurrent,
        setNext,
        setConfirm,
        setShowCurrent,
        setShowNext,
        setShowConfirm,

        // handlers
        handleSubmit,
        handleCancel,
    };
}
